import { useMemo } from 'react'

import { FeatureFlag, useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useRouter } from 'next/router'

import { useAppSelector } from '@/redux/hooks'
import { selectCompletedFtuIds } from '@/redux/ui/uiSlice'

import type { FtuConfig } from '../types/types'

type ActiveFtuContext = {
  completedIds: string[]
  pathname: string
  isFlagOn: (flag?: FeatureFlag) => boolean
}

/**
 * Pure selection of the active FTU: the first eligible config wins (registry
 * order = priority). A config is eligible when its flag is on, it hasn't been
 * completed, its route matches (or it has none), and it has at least one step.
 */
export const selectActiveFtu = (
  ftus: FtuConfig[],
  { completedIds, pathname, isFlagOn }: ActiveFtuContext
): FtuConfig | null =>
  ftus.find(
    (ftu) =>
      ftu.steps.length > 0 &&
      isFlagOn(ftu.featureFlag) &&
      !completedIds.includes(ftu.id) &&
      (ftu.route === undefined || ftu.route === pathname)
  ) ?? null

export const useActiveFtu = (ftus: FtuConfig[]): FtuConfig | null => {
  const flags = useFeatureFlags()
  const completedIds = useAppSelector(selectCompletedFtuIds)
  const { pathname } = useRouter()

  return useMemo(
    () =>
      selectActiveFtu(ftus, {
        completedIds,
        pathname,
        isFlagOn: (flag) => !flag || Boolean(flags[flag]),
      }),
    [ftus, completedIds, pathname, flags]
  )
}
